// src/features/pdf/pdf-reorder/components/DraggablePageItem.tsx
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Paper, Box, Typography, IconButton, Tooltip, Skeleton } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

interface DraggablePageItemProps {
    id: string;
    originalPageNum: number;
    finalPageNum: number;
    isDeleted: boolean;
    thumbnailUrl: string | null;
    onDelete: (id: string) => void;
    onPreview: (id: string) => void;
}

const DraggablePageItem: React.FC<DraggablePageItemProps> = ({
                                                                 id,
                                                                 originalPageNum,
                                                                 finalPageNum,
                                                                 isDeleted,
                                                                 thumbnailUrl,
                                                                 onDelete,
                                                                 onPreview,
                                                             }) => {
    const { t } = useTranslation();
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 2 : 'auto',
        opacity: isDeleted ? 0.5 : 1,
        cursor: 'pointer',
    };

    return (
        <Paper
            ref={setNodeRef}
            style={style}
            elevation={isDragging ? 8 : 2}
            sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                width: 140,
                height: 200, // A4-like aspect ratio
                p: 1,
                userSelect: 'none',
                bgcolor: 'background.default',
                border: theme => `1px solid ${theme.palette.divider}`,
                overflow: 'hidden',
                '&:hover': {
                    borderColor: 'primary.main',
                },
            }}
        >
            {/* Drag Handle */}
            <Box
                {...attributes}
                {...listeners}
                sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    cursor: 'grab',
                    touchAction: 'none', // For mobile devices
                    color: 'text.secondary',
                }}
                onClick={(e) => e.stopPropagation()} // Prevent preview when dragging
            >
                <DragIndicatorIcon />
            </Box>

            {/* Delete Button */}
            <Tooltip title={'Delete'} placement="top">
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(id);
                    }}
                    sx={{ position: 'absolute', top: 4, right: 4 }}
                    aria-label="delete page"
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            {/* Top: Final Page Number (new position, bold) */}
            <Typography variant="body2" color="text.primary" align="center" sx={{ fontWeight: 'bold' }}>
                {isDeleted ? '-' : `${t('pdf_reorder_page_final', { ns: 'tools' })}: ${finalPageNum}`}
            </Typography>

            {/* Center: Main Preview Area */}
            <Box
                onClick={() => onPreview(id)}
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    my: 1,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: theme => `1px solid ${theme.palette.divider}`,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {isDeleted ? (
                    <Typography variant="h6" color="error.main" sx={{ fontWeight: 'bold' }}>
                        {t('pdf_reorder_page_deleted', { ns: 'tools' })}
                    </Typography>
                ) : thumbnailUrl ? (
                    <img
                        src={thumbnailUrl}
                        alt={`Page ${originalPageNum}`}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain'
                        }}
                    />
                ) : (
                    // Skeleton loader while thumbnail is generating
                    <Skeleton variant="rectangular" width="100%" height="100%" />

                )}
            </Box>

            {/* Bottom: Original Page Number (new position) */}
            <Typography variant="caption" color="text.secondary" align="center">
                {t('pdf_reorder_page_original', { ns: 'tools' })}: {originalPageNum}
            </Typography>

        </Paper>
    );
};

export default DraggablePageItem;